import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { formatPrice } from '@src/quince-site-common/src/utils/formatPrice'

import styles from './ProductInfoPrice.module.scss'

export interface ProductInfoPriceProps {
  formattedSalePrice: string
  formattedTraditionalPrice?: string
  currency?: string
  savingPercentage?: number
  formattedFestivalOfferPrice?: string
  showDiscount?: boolean
  pricePerServing?: number
  traditionalPricePerServing?: number
  isVitamin?: boolean
  hideSavings?: boolean
}

const DIFFERENCE_TO_IGNORE = 0.1

function ProductInfoPrice({
  formattedSalePrice,
  formattedTraditionalPrice,
  savingPercentage,
  formattedFestivalOfferPrice,
  showDiscount = true,
  pricePerServing,
  traditionalPricePerServing,
  isVitamin,
  hideSavings,
}: ProductInfoPriceProps) {
  const { t } = useTranslation('common')
  const isDiscountLabelPresent =
    showDiscount && formattedTraditionalPrice
      ? Number(formattedTraditionalPrice) - Number(formattedSalePrice) > DIFFERENCE_TO_IGNORE
      : false

  // NOTE: Not covering festivalOFferPrice design for vitamin. BE will later make changes for pps.
  // Since no sale is coming soon, will cover this once BE changes are done.
  const hasVitaminPricing = isVitamin && !!pricePerServing && !!traditionalPricePerServing
  const vitaminSavingsPercentage = hasVitaminPricing
    ? productHelper.getSavingPercentage(traditionalPricePerServing, pricePerServing)
    : ''

  const showSavingPercentage =
    !formattedFestivalOfferPrice &&
    formattedTraditionalPrice &&
    savingPercentage &&
    isDiscountLabelPresent &&
    !hasVitaminPricing &&
    !hideSavings

  return (
    <div>
      <div
        className={classnames(styles.priceWrapper, {
          [styles.priceWrapper__festivalOffer]: formattedFestivalOfferPrice,
        })}
      >
        <div className={classnames(styles.priceWrapper__main, styles.priceWrapper__main__space)}>
          <div className={classnames(styles.priceWrapper__main__value, styles.priceWrapper__main__value__qp)}>
            <span className="sales_price_selector_for_force_update">{formatPrice(formattedSalePrice)}</span>
            {hasVitaminPricing && !formattedFestivalOfferPrice && (
              <span className={styles.priceWrapper__main__value__perserving}>
                {'('}
                <span className={styles.priceWrapper__main__value__perserving__traditional}>
                  {formatPrice(traditionalPricePerServing)}{' '}
                </span>
                {formatPrice(pricePerServing)} {t('serving_v2')}
                {')'}
              </span>
            )}
          </div>
          {formattedFestivalOfferPrice && (
            <div
              className={classnames(
                styles.priceWrapper__main__value,
                styles.priceWrapper__main__value__qp,
                styles.festival_offer,
              )}
            >
              <span className="festival_sale_price_selector_for_force_update">
                {formatPrice(formattedFestivalOfferPrice)}
              </span>
            </div>
          )}
          {showSavingPercentage && (
            <div className={styles.priceWrapper__main__text}>{t('you_save', { percentage: savingPercentage })}</div>
          )}
          {vitaminSavingsPercentage && !formattedFestivalOfferPrice && !hideSavings && (
            <div className={styles.priceWrapper__main__text}>
              {t('save')} <span>{vitaminSavingsPercentage}</span>
              {t('compared_to_traditional_retail')}
            </div>
          )}
        </div>

        {formattedTraditionalPrice && isDiscountLabelPresent && !hasVitaminPricing && (
          <div className={classnames(styles.priceWrapper__main, styles.priceWrapper__main__margin)}>
            <div className={classnames(styles.priceWrapper__main__value, styles.priceWrapper__main__value__rp)}>
              <span className="traditionalPrice_selector_for_force_update">
                {formatPrice(formattedTraditionalPrice)}
              </span>
            </div>
            <div className={classnames(styles.priceWrapper__main__text, styles.priceWrapper__main__text__rp)}>
              {t('traditional_retail')}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductInfoPrice
